import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { NavRoutes } from '../../constants/nav.routes';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PinDialogComponent } from '../pin-dialog/pin-dialog.component';
import { ErrorResponseHandler } from 'src/app/shared/services/error-response-handler';
import { KioskService } from 'src/openapi-gen/services';
import { Router, RouterOutlet } from '@angular/router';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { CommonModule } from '@angular/common';
import { KioskUserService } from 'src/app/shared/services/kiosk-user.service';
import { KioskRole } from 'src/openapi-gen/models';
import { IdleService } from 'src/app/shared/services/idle-service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppState } from 'src/app/shared/services/app-state';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-kiosk-page',
  templateUrl: './kiosk-page.component.html',
  styleUrls: ['./kiosk-page.component.scss'],
  providers: [IdleService],
  standalone: true,
  imports: 
  [
    CommonModule,
    FlexModule, 
    RouterOutlet, 

    MatToolbarModule, 
    MatButtonModule, 
    MatDialogModule, 
    MatIconModule,
  ]
})
export class KioskPageComponent extends BaseComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  readonly routes = NavRoutes;
  screenWidth: number;

  displayName: string;
  kioskRole: KioskRole;
  isDoubleCountAgency: boolean = false;

  get isAgency(): boolean { 
    return this.kioskRole === KioskRole.Agency; 
  }
  
  get isUser(): boolean { 
    return this.kioskRole === KioskRole.User; 
  }
  
  constructor(
    private idleService: IdleService,
    private router: Router, 
    private dialog: MatDialog,
    private errorResponseHandler: ErrorResponseHandler,
    private kioskService: KioskService,
    private kioskUserService: KioskUserService,
    public appState: AppState
  ) { 
    super();
    this.screenWidth = window.innerWidth;
    window.onresize = () => {
        // set screenWidth on screen size change
        this.screenWidth = window.innerWidth;
    };

    this.idleService.setTimeoutOperation(() => { 
      this.logoutAffiliatedUser(); 
      this.idleService.stopIdleChecking();
    });
  }

  ngOnInit() {
    this.initializeUser();
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.initializeUser();
    });
  }

  private initializeUser() {
    let affiliatedUser = this.kioskUserService.getAffiliatedUser();
    if(affiliatedUser !== null) {
      let maxCharactersToShow = this.MAX_DISPLAY_CHARACTER_LIMIT * 2;
      this.displayName = affiliatedUser.displayName.length > maxCharactersToShow ? `${affiliatedUser.displayName.substring(0, maxCharactersToShow)}…` : affiliatedUser.displayName;
      this.kioskRole = affiliatedUser.role as KioskRole;
      this.isDoubleCountAgency = affiliatedUser.isDoubleCountAgency;
      
      if(affiliatedUser.disallowMultipleTransactions) {
        this.idleService.startIdleCheckingShort();
      }
      else {
        this.idleService.startIdleCheckingLong();
      }
    }
    else {
      this.displayName = null;
      this.kioskRole = null;
      this.isDoubleCountAgency = false;
      this.idleService.stopIdleChecking();
    }
  }

  ngOnDestroy() {
    this.idleService.stopIdleChecking();
  }

  logoutAffiliatedUser() {
    this.appState.setLoadingState(true);
    this.kioskService
      .apiKioskLogoutAlternatePost()
      .subscribe({
        next: _ => {
          this.kioskUserService.clearSelection();
          this.appState.setLoadingState(false);
          this.router.navigate([NavRoutes.Kiosk], { replaceUrl: true });
        },
        error: (err) => {
          this.kioskUserService.clearSelection();
          this.appState.setLoadingState(false);
          this.errorResponseHandler.handle_kiosk_logout_alternative(err);
        }
      });
  }

  openLogoutUserDialog() {
    this.dialog.open(PinDialogComponent, {
      closeOnNavigation: true,
      disableClose: true,
      data: {
        message: "STAFF ONLY: To continue, please enter your staff PIN:",
        isLoggingOut: true
      },
    });
  }
}
